import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext(null);

export const TIER_CONFIG = {
  FREE: {
    name: 'Khách (Free)',
    color: 'text-slate-400 bg-slate-800/80 border-slate-700',
    badge: 'FREE',
    maxCandles: 5000,
    hasIndicators: true,
    hasStrategies: true,
    hasPaperTrading: true,
    hasDeepBacktest: false,
    hasPineEditor: false,
    hasTelegramAlerts: false,
    hasAIAnalysis: true,
  },
  BASIC: {
    name: 'Thành viên Basic',
    color: 'text-cyan-400 bg-cyan-950/40 border-cyan-500/40',
    badge: 'BASIC',
    maxCandles: 10000,
    hasIndicators: true,
    hasStrategies: true,
    hasPaperTrading: true,
    hasDeepBacktest: false,
    hasPineEditor: false,
    hasTelegramAlerts: false,
    hasAIAnalysis: true,
  },
  PRO: {
    name: 'Thành viên PRO 👑',
    color: 'text-amber-300 bg-amber-950/40 border-amber-500/50',
    badge: 'PRO 👑',
    maxCandles: 50000,
    hasIndicators: true,
    hasStrategies: true,
    hasPaperTrading: true,
    hasDeepBacktest: true,
    hasPineEditor: true,
    hasTelegramAlerts: true,
    hasAIAnalysis: true,
  },
  ADMIN: {
    name: 'Quản Trị Viên ⚡',
    color: 'text-purple-300 bg-purple-950/50 border-purple-500/50',
    badge: 'ADMIN ⚡',
    maxCandles: 100000,
    hasIndicators: true,
    hasStrategies: true,
    hasPaperTrading: true,
    hasDeepBacktest: true,
    hasPineEditor: true,
    hasTelegramAlerts: true,
    hasAIAnalysis: true,
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#admin') {
      return 'admin';
    }
    return 'chart';
  });

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState('login'); // 'login' | 'register'
  const [userProfileModalOpen, setUserProfileModalOpen] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [tierGateOpen, setTierGateOpen] = useState(false);
  const [tierGateFeature, setTierGateFeature] = useState({ name: '', requiredTier: 'BASIC' });

  // Sync hash change
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#admin') {
        setCurrentView('admin');
      } else {
        setCurrentView('chart');
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigateTo = (view) => {
    setCurrentView(view);
    if (view === 'admin') {
      window.location.hash = 'admin';
    } else {
      window.location.hash = '';
    }
  };

  const parseTokenPayload = (token) => {
    try {
      if (!token || !token.includes('.')) return null;
      let b64 = token.split('.')[0].replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) {
        b64 += '=';
      }
      const jsonStr = decodeURIComponent(
        atob(b64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonStr);
      if (payload.exp && payload.exp < Date.now() / 1000) {
        return null; // Expired
      }
      return payload;
    } catch (e) {
      return null;
    }
  };

  // Load user on start from token
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('minopro_token');
      if (token) {
        const payload = parseTokenPayload(token);
        if (!payload) {
          // Token expired or malformed
          localStorage.removeItem('minopro_token');
          setUser(null);
          setLoading(false);
          return;
        }

        // Set local user state immediately from valid token to prevent flashing / accidental logout
        setUser({
          id: payload.sub,
          username: payload.username,
          tier: payload.tier || 'BASIC',
          status: 'ACTIVE'
        });

        try {
          const res = await api.getMe();
          if (res.success && res.user) {
            setUser(res.user);
            if (res.refreshed_token) {
              try {
                localStorage.setItem('minopro_token', res.refreshed_token);
              } catch (e) {}
            }
            if (res.user.timezone) {
              try {
                localStorage.setItem('trading_timezone', res.user.timezone);
                localStorage.setItem(`minopro_timezone_${res.user.id}`, res.user.timezone);
              } catch (e) {}
            }
          } else {
            localStorage.removeItem('minopro_token');
            setUser(null);
          }
        } catch (err) {
          const status = err.response?.status;
          // Only clear token if server explicitly returned 401 Unauthorized or 403 Forbidden
          if (status === 401 || status === 403) {
            console.warn('Auth token rejected by server (401/403):', err);
            localStorage.removeItem('minopro_token');
            setUser(null);
          } else {
            // Transient network or server restart error (e.g. 502 Bad Gateway) -> keep user session active!
            console.warn('Network or server transient error during auth check, preserving user session:', err);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // Periodic user activity ping (every 60s while logged in and tab is active)
  useEffect(() => {
    if (!user || !user.id) return;
    
    const sendPing = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        api.pingUserActivity().catch(() => {});
      }
    };

    // Initial ping
    sendPing();

    const interval = setInterval(sendPing, 60000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendPing();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id]);

  const login = async (identifier, password) => {
    const res = await api.login(identifier, password);
    if (res.success && res.token) {
      localStorage.setItem('minopro_token', res.token);
      setUser(res.user);
      if (res.user?.timezone) {
        try {
          localStorage.setItem('trading_timezone', res.user.timezone);
          localStorage.setItem(`minopro_timezone_${res.user.id}`, res.user.timezone);
        } catch (e) {}
      }
      setAuthModalOpen(false);
      return res.user;
    }
    throw new Error(res.message || 'Đăng nhập thất bại.');
  };

  const register = async (username, email, password) => {
    const res = await api.register(username, email, password);
    if (res.success && res.token) {
      localStorage.setItem('minopro_token', res.token);
      setUser(res.user);
      if (res.user?.timezone) {
        try {
          localStorage.setItem('trading_timezone', res.user.timezone);
          localStorage.setItem(`minopro_timezone_${res.user.id}`, res.user.timezone);
        } catch (e) {}
      }
      setAuthModalOpen(false);
      return res.user;
    }
    throw new Error(res.message || 'Đăng ký thất bại.');
  };

  const loginWithGoogle = async (credential) => {
    const res = await api.googleLogin(credential);
    if (res.success && res.token) {
      localStorage.setItem('minopro_token', res.token);
      setUser(res.user);
      if (res.user?.timezone) {
        try {
          localStorage.setItem('trading_timezone', res.user.timezone);
          localStorage.setItem(`minopro_timezone_${res.user.id}`, res.user.timezone);
        } catch (e) {}
      }
      setAuthModalOpen(false);
      return res.user;
    }
    throw new Error(res.message || 'Đăng nhập Google thất bại.');
  };

  const forgotPassword = async (email) => {
    return await api.forgotPassword(email);
  };

  const resetPassword = async (email, otp, newPassword) => {
    return await api.resetPassword(email, otp, newPassword);
  };

  const logout = () => {
    localStorage.removeItem('minopro_token');
    setUser(null);
    setUserProfileModalOpen(false);
    setAdminPanelOpen(false);
  };

  const changePassword = async (oldPassword, newPassword) => {
    return await api.changePassword(oldPassword, newPassword);
  };

  const updateUserTimezone = async (newTimezone) => {
    try {
      localStorage.setItem('trading_timezone', newTimezone);
      if (user?.id) {
        localStorage.setItem(`minopro_timezone_${user.id}`, newTimezone);
      }
      setUser((prev) => (prev ? { ...prev, timezone: newTimezone } : prev));
      if (user) {
        await api.updateTimezone(newTimezone).catch(() => {});
      }
    } catch (e) {
      console.warn("Update user timezone error:", e);
    }
  };

  const updateUserTelegramChatId = async (newChatId) => {
    try {
      const cleanId = String(newChatId || '').trim();
      setUser((prev) => (prev ? { ...prev, telegram_chat_id: cleanId } : prev));
      if (user?.id) {
        localStorage.setItem(`minopro_telegram_chat_id_${user.id}`, cleanId);
      }
      return await api.updateUserTelegram(cleanId);
    } catch (e) {
      console.warn("Update user telegram error:", e);
      throw e;
    }
  };

  const currentTier = user ? user.tier : 'FREE';
  const tierInfo = TIER_CONFIG[currentTier] || TIER_CONFIG.FREE;

  const canAccess = (feature) => {
    if (currentTier === 'ADMIN' || currentTier === 'PRO') return true;
    if (currentTier === 'BASIC') {
      if (['PINE_EDITOR', 'TELEGRAM_ALERTS', 'ADMIN_PANEL', 'DEEP_BACKTEST'].includes(feature)) {
        return false;
      }
      return true;
    }
    // FREE User (Guest)
    if (['LIVE_CHART', 'TICK_STREAM', 'MTF_MATRIX', 'AI_ANALYSIS', 'INDICATORS', 'STRATEGIES'].includes(feature)) {
      return true;
    }
    return false;
  };

  const openAuth = (tab = 'login') => {
    setAuthModalTab(tab);
    setAuthModalOpen(true);
  };

  const promptTierGate = (featureName, requiredTier = 'PRO') => {
    setTierGateFeature({ name: featureName, requiredTier });
    setTierGateOpen(true);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        tier: currentTier,
        tierInfo,
        isAuthenticated: !!user,
        isAdmin: user?.tier === 'ADMIN',
        isPro: user?.tier === 'PRO' || user?.tier === 'ADMIN',
        isBasic: !!user,
        login,
        register,
        loginWithGoogle,
        forgotPassword,
        resetPassword,
        logout,
        changePassword,
        updateUserTimezone,
        updateUserTelegramChatId,
        canAccess,
        openAuth,
        promptTierGate,
        currentView,
        setCurrentView,
        navigateTo,
        authModalOpen,
        setAuthModalOpen,
        authModalTab,
        setAuthModalTab,
        userProfileModalOpen,
        setUserProfileModalOpen,
        adminPanelOpen,
        setAdminPanelOpen,
        tierGateOpen,
        setTierGateOpen,
        tierGateFeature,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
